'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft, LayoutDashboard, LogOut } from 'lucide-react';

import { Logo } from '@/components/ui/logo';
import { authClient } from '@/lib/auth/client';

function getDashboardUrl(role?: string | null): string | null {
  if (role === 'admin') return '/admin/dashboard';
  if (role === 'investor') return '/investor/dashboard';
  if (role === 'founder') return '/founder/dashboard';
  return null;
}

/**
 * Session-aware header for the apply layout.
 * Shows dashboard link + sign out for authenticated users,
 * or just "Back to Home" for unauthenticated visitors.
 */
export function ApplyHeader() {
  const router = useRouter();
  const [session, setSession] = useState<{
    user: { role?: string | null };
  } | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    authClient
      .getSession()
      .then((res) => setSession(res.data ?? null))
      .finally(() => setReady(true));
  }, []);

  const dashboardUrl = getDashboardUrl(session?.user?.role);

  return (
    <header className="sticky top-0 z-20 bg-background/75 backdrop-blur-sm border-b border-border/50 flex-none">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo imageClassName="h-8 w-auto" />

        <div className="flex items-center gap-4">
          {ready && session ? (
            <>
              {dashboardUrl && (
                <Link
                  href={dashboardUrl}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Link>
              )}
              <button
                onClick={async () => {
                  await authClient.signOut();
                  router.push('/auth/login');
                }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </>
          ) : (
            <Link
              href="/"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Home
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
